import { useState } from 'react';
import { Box, Button, HStack, IconButton, Text, Wrap } from '@chakra-ui/react';
import { X } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { blockedIdsAtom, memberLabelsAtom } from '../../store';
import {
  activeNudge,
  dismissNudge,
  isNudgeDismissed,
  nudgeGreetParams,
  nudgePeople,
  type NudgePerson,
} from '../../lib/siblingNudges';
import {
  FESTIVAL_DAY_NAMES,
  findOccasion,
  occasionName,
} from '../../lib/greetings';
import { translate, useLanguage, useT } from '../../i18n';

interface SiblingNudgeCardProps {
  /** Everyone in the current family. */
  members: ReadonlyArray<{ userId: string; user: { displayName: string } }>;
  currentUserId: string | undefined;
  /**
   * Their direct chat, opened or started, with the greeting sheet on this
   * festival: `params` are the chat link's (`greet`, `greetDay`).
   */
  onWish: (userId: string, params: Record<string, string>) => void;
  /** For tests: the device's date. */
  now?: Date;
}

// Rakhi and Bhai Dooj nudges (M75, D313–D316): on the day and the day
// before, one gentle card for the people you call a brother or sister
// (D143), worked out on this device. Dismissed for that festival and year;
// never a push. Kept apart from the home header (festival mode, #49).
export function SiblingNudgeCard({
  members,
  currentUserId,
  onWish,
  now,
}: SiblingNudgeCardProps) {
  const t = useT();
  const language = useLanguage();
  const labels = useAtomValue(memberLabelsAtom);
  const blocked = useAtomValue(blockedIdsAtom);
  const nudge = activeNudge(now);
  const [dismissed, setDismissed] = useState<string | null>(null);
  if (!nudge || dismissed === nudge.key || isNudgeDismissed(nudge.key)) {
    return null;
  }
  const people = nudgePeople(members, labels, currentUserId, blocked);
  if (people.length === 0) return null;

  const occasion = findOccasion(nudge.occasion)!;
  const day = nudge.day
    ? occasion.days?.find((d) => d.id === nudge.day)
    : undefined;
  const festival = day
    ? translate(language, FESTIVAL_DAY_NAMES[day.id])
    : occasionName(occasion, language);
  const motif = (day ?? occasion).designs[0]?.motif[0] ?? '🎁';
  const params = nudgeGreetParams(nudge);
  const one = people.length === 1 ? people[0]! : null;
  const wishLabel = (person: NudgePerson) =>
    t('chat.siblings.wishName', { name: person.name, festival });

  const dismiss = () => {
    dismissNudge(nudge.key, now);
    setDismissed(nudge.key);
  };

  return (
    <Box px={4} pb={3}>
      <Box
        role="region"
        aria-label={t('chat.siblings.label')}
        position="relative"
        pl={4}
        pr={12}
        py={3}
        borderRadius="card"
        bg="surface"
        borderWidth="1px"
        borderColor="line"
      >
        <HStack spacing={3} align="center" minH="44px">
          <Text as="span" fontSize="26px" aria-hidden="true">
            {motif}
          </Text>
          <Text
            flex="1"
            minW={0}
            fontWeight={700}
            fontSize="16px"
            lineHeight={1.3}
          >
            {one
              ? t('chat.siblings.one', { name: one.name, festival })
              : t('chat.siblings.many', { festival })}
          </Text>
          {one && (
            <Button
              h="44px"
              px={4}
              flexShrink={0}
              onClick={() => onWish(one.userId, params)}
              aria-label={wishLabel(one)}
            >
              {t('chat.siblings.wish')}
            </Button>
          )}
        </HStack>
        {!one && (
          <Wrap as="ul" listStyleType="none" spacing={2} mt={2}>
            {people.map((person) => (
              <Box as="li" key={person.userId}>
                <Button
                  h="40px"
                  px={4}
                  variant="outline"
                  borderRadius="full"
                  onClick={() => onWish(person.userId, params)}
                  aria-label={wishLabel(person)}
                >
                  {person.name}
                </Button>
              </Box>
            ))}
          </Wrap>
        )}
        <IconButton
          aria-label={t('chat.siblings.dismiss')}
          title={t('chat.siblings.dismiss')}
          icon={<X size={18} />}
          size="md"
          variant="ghost"
          colorScheme="gray"
          color="muted"
          borderRadius="full"
          position="absolute"
          top="6px"
          right="4px"
          onClick={dismiss}
        />
      </Box>
    </Box>
  );
}
